import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import type { Tone } from "@/types/tone";

type SurfaceTone =
  | "base"
  | "muted"
  | Extract<
      Tone,
      "neutral" | "positive" | "warning" | "negative" | "informative"
    >;

const surfaceVariants = cva("text-surface-on-surface", {
  variants: {
    tone: {
      base: ["bg-surface-container-lowest"],
      muted: ["bg-surface-container-low"],
      neutral: ["bg-surface-container"],
      positive: ["bg-positive-container", "text-positive-on-container"],
      warning: [
        "bg-accessibility-warning-container",
        "text-warning-on-container",
      ],
      negative: ["bg-negative-container", "text-negative-on-container"],
      informative: [
        "bg-informative-container",
        "text-informative-on-container",
      ],
    },
    padding: {
      none: "p-0",
      dense: "px-(--spacing-8) py-(--spacing-10)",
      compact: "p-(--spacing-12)",
      comfortable: "px-(--spacing-20) py-(--spacing-16)",
    },
    radius: {
      none: "rounded-(--radius-none)",
      sm: "rounded-(--radius-sm)",
      md: "rounded-(--radius-md)",
      lg: "rounded-(--radius-lg)",
      xl: "rounded-(--radius-xl)",
      full: "rounded-(--radius-full)",
    },
    aspect: {
      auto: null,
      square: "aspect-square",
    },
    centered: {
      true: "grid place-items-center",
      false: null,
    },
    width: {
      auto: null,
      content: "w-fit",
      full: "w-full",
    },
  },
  defaultVariants: {
    tone: "base",
    padding: "comfortable",
    radius: "sm",
    aspect: "auto",
    centered: false,
    width: "auto",
  },
});

type SurfaceAspect = NonNullable<
  VariantProps<typeof surfaceVariants>["aspect"]
>;
type SurfacePadding = NonNullable<
  VariantProps<typeof surfaceVariants>["padding"]
>;
type SurfaceRadius = NonNullable<
  VariantProps<typeof surfaceVariants>["radius"]
>;
type SurfaceWidth = NonNullable<VariantProps<typeof surfaceVariants>["width"]>;

export interface SurfaceProps
  extends Omit<
      React.HTMLAttributes<HTMLDivElement>,
      "className" | "color" | "style"
    >,
    VariantProps<typeof surfaceVariants> {
  children?: React.ReactNode;
}

/**
 * Lightweight token-backed content surface.
 *
 * Use when content needs a semantic background, padding, and radius without
 * card border/elevation or alert semantics.
 *
 * @example
 * <Surface tone="base">Content</Surface>
 * <Surface tone="informative" padding="compact" radius="md">Content</Surface>
 * <Surface aspect="square" centered>Content</Surface>
 * <Surface width="content">Content-sized surface</Surface>
 */
function Surface({
  tone = "base",
  padding = "comfortable",
  radius = "sm",
  aspect = "auto",
  centered = false,
  width = "auto",
  children,
  ...props
}: SurfaceProps) {
  return (
    <div
      data-slot="surface"
      data-tone={tone}
      data-padding={padding}
      data-radius={radius}
      data-aspect={aspect}
      data-centered={centered || undefined}
      data-width={width}
      className={cn(
        surfaceVariants({ tone, padding, radius, aspect, centered, width }),
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export { Surface };
export type {
  SurfaceAspect,
  SurfacePadding,
  SurfaceRadius,
  SurfaceTone,
  SurfaceWidth,
};
